import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth.store';

const API_URL = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data;
          useAuthStore.getState().setAccessToken(accessToken);

          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        } catch {
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      } else {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
// Note: no register() here on purpose — org/owner bootstrap is a one-time,
// backend-only operation (POST /api/auth/register directly), not a public
// signup flow. Ongoing account creation goes through usersApi.create (Team
// page "Add Employee"), which requires an authenticated ADMIN+.
export const authApi = {
  login: (data: { email: string; password: string }) =>
    apiClient.post('/api/auth/login', data),

  logout: () => apiClient.post('/api/auth/logout'),

  me: () => apiClient.get('/api/auth/me'),

  // Redeems a one-time ticket handed off from apace-rms for a real
  // session — see /sso/consume route and useSsoConsume().
  ssoConsume: (ticket: string) => apiClient.post('/api/auth/sso/consume', { ticket }),
};

// Organizations API
export const organizationsApi = {
  getCurrent: () => apiClient.get('/api/organizations/me'),
  getMembers: () => apiClient.get('/api/organizations/members'),
};

// Devices API
export const devicesApi = {
  list: () => apiClient.get('/api/devices'),
  getMyDevices: () => apiClient.get('/api/devices/my'),
  getById: (id: string) => apiClient.get(`/api/devices/${id}`),
  createCode: (data?: { assignedUserId?: string; expiresInHours?: number }) =>
    apiClient.post('/api/devices/codes', data || {}),
  getCodes: () => apiClient.get('/api/devices/codes'),
  update: (id: string, data: { name?: string; isActive?: boolean }) =>
    apiClient.patch(`/api/devices/${id}`, data),
  delete: (id: string, force = false) =>
    apiClient.delete(`/api/devices/${id}`, { params: force ? { force: 'true' } : undefined }),
  getInstallScript: (platform: string = 'win32') =>
    apiClient.get('/api/devices/install-script', { params: { platform } }),
  reset: (id: string) => apiClient.post(`/api/devices/${id}/reset`),
};

// Screenshots API
export const screenshotsApi = {
  list: (params?: {
    userId?: string;
    deviceId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/api/screenshots', { params }),

  getById: (id: string) => apiClient.get(`/api/screenshots/${id}`),

  delete: (id: string) => apiClient.delete(`/api/screenshots/${id}`),

  // "Take a live screenshot now" — see components/dashboard's camera
  // button on the Screenshots detail page. requestLive() kicks it off;
  // getRequestStatus() is a poll fallback for if the realtime socket
  // event is ever missed (backgrounded tab, dropped message).
  requestLive: (deviceId: string) => apiClient.post('/api/screenshots/requests', { deviceId }),

  getRequestStatus: (requestId: string) => apiClient.get(`/api/screenshots/requests/${requestId}`),
};

// Activity API
export const activityApi = {
  getSummary: (params?: {
    userId?: string;
    deviceId?: string;
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/api/activity/summary', { params }),

  getTimeline: (params?: {
    userId?: string;
    deviceId?: string;
    startDate?: string;
    endDate?: string;
    granularity?: 'hour' | 'day' | 'week';
  }) => apiClient.get('/api/activity/timeline', { params }),

  getPerUserSummary: (params?: {
    startDate?: string;
    endDate?: string;
  }) => apiClient.get('/api/activity/per-user-summary', { params }),
};

// Logs API
export const logsApi = {
  getLogs: (params?: {
    deviceId?: string;
    userId?: string;
    level?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/api/logs', { params }),
};

// Users API
export const usersApi = {
  create: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) => apiClient.post('/api/users', data),
};

// Attendance API
export const attendanceApi = {
  login: () => apiClient.post('/api/attendance/login'),
  startBreak: () => apiClient.post('/api/attendance/break'),
  resume: () => apiClient.post('/api/attendance/resume'),
  logout: () => apiClient.post('/api/attendance/logout'),
  getToday: () => apiClient.get('/api/attendance/today'),
  getHistory: (params?: {
    userId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/api/attendance/history', { params }),
};

// Releases API
export const releasesApi = {
  list: () => apiClient.get('/api/releases'),

  upload: (formData: FormData) =>
    apiClient.post('/api/releases', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deactivate: (id: string) => apiClient.delete(`/api/releases/${id}`),

  pushUpdate: (data: { releaseId: string; targetDeviceId?: string }) =>
    apiClient.post('/api/releases/push-update', data),

  getUpdateCommands: (params?: {
    deviceId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => apiClient.get('/api/releases/update-commands', { params }),
};
